import { ref, watch, computed, nextTick, onBeforeUnmount } from "vue"

import { getBoundingBoxes, getTopHighlightBlockId } from "./page-helper"

export const useSetupPage = ({
  isShowExtractedWords,
  isForceShowHighlightBlocks,
  keyboardState,
  seeSourceAttributeSlug,
  containerState,
  pagingState,
  currentPageState,
  scalingState,
  wordTooltipState,
  canvasWrapper,
  canvas
}) => {
  const image = ref(null)

  // Because the size of the wrapper is not determined until the scale is determined.
  const isScaleDefined = computed(() => {
    return typeof scalingState.scale.value !== "undefined"
  })
  let isInitialScrolled = false

  const onCanvasMouseMove = event => {
    if (
      !isShowExtractedWords.value &&
      !isForceShowHighlightBlocks.value &&
      typeof seeSourceAttributeSlug.value === "undefined"
    ) {
      return
    }

    if (keyboardState.isSpaceKeyPressing.value) return

    const { offsetX, offsetY } = event

    const hitBoundingBox = getBoundingBoxes({
      positionalText: currentPageState.positionalText.value,
      highlightBlockIds: currentPageState.highlightBlockIds.value,
      highlightBlockScores: currentPageState.highlightBlockScores.value,
      attributeSources: currentPageState.attributeSources.value,
      isShowExtractedWords: isShowExtractedWords.value,
      isForceShowHighlightBlocks: isForceShowHighlightBlocks.value,
      seeSourceAttributeSlug: seeSourceAttributeSlug.value
    }).find(({ boundingBox }) => {
      const isInsideX = boundingBox[0].x <= offsetX && offsetX <= boundingBox[2].x
      const isInsideY = boundingBox[0].y <= offsetY && offsetY <= boundingBox[2].y
      return isInsideX && isInsideY
    })

    if (!hitBoundingBox) {
      wordTooltipState.boundingBox.value = undefined
      return
    }

    if (wordTooltipState.boundingBox.value && wordTooltipState.boundingBox.value.id === hitBoundingBox.id) {
      return
    }

    wordTooltipState.boundingBox.value = hitBoundingBox
  }

  const getBoundingBoxScrollPosition = boundingBoxId => {
    if (
      !containerState.element.value ||
      !containerState.width.value ||
      !containerState.height.value ||
      !scalingState.scale.value
    ) {
      return
    }

    const boundingBox = getBoundingBoxes({
      positionalText: currentPageState.positionalText.value,
      highlightBlockIds: currentPageState.highlightBlockIds.value,
      highlightBlockScores: currentPageState.highlightBlockScores.value,
      attributeSources: currentPageState.attributeSources.value,
      isShowExtractedWords: isShowExtractedWords.value,
      isForceShowHighlightBlocks: isForceShowHighlightBlocks.value,
      seeSourceAttributeSlug: seeSourceAttributeSlug.value
    }).find(({ id }) => {
      return id === boundingBoxId
    })

    if (!boundingBox) return

    const top = (boundingBox.boundingBox[2].y * 0.5 + boundingBox.boundingBox[0].y * 0.5) * scalingState.scale.value
    const left = (boundingBox.boundingBox[2].x * 0.5 + boundingBox.boundingBox[0].x * 0.5) * scalingState.scale.value

    return {
      scrollTop: top - containerState.height.value / 2,
      scrollLeft: left - containerState.width.value / 2
    }
  }

  // When the page changes, or the seeSource attribute changes, let's reset the isInitialScrolled
  watch(
    pagingState.currentPage,
    () => {
      isInitialScrolled = false
    },
    { immediate: true }
  )

  watch(
    seeSourceAttributeSlug,
    seeSourceAttributeSlug => {
      if (typeof seeSourceAttributeSlug === "undefined") return

      isInitialScrolled = false
    },
    { immediate: true }
  )

  // When the canvas changes, let's fix the listeners
  watch(
    canvas,
    (canvas, oldCanvas) => {
      if (oldCanvas) {
        oldCanvas.removeEventListener("mousemove", onCanvasMouseMove)
      }

      if (canvas) {
        canvas.addEventListener("mousemove", onCanvasMouseMove)
      }
    },
    { immediate: true }
  )

  watch(
    currentPageState.imageUrl,
    imageUrl => {
      image.value = null

      if (!imageUrl) return

      const imageElement = new Image()
      imageElement.onload = () => {
        currentPageState.width.value = imageElement.naturalWidth
        currentPageState.height.value = imageElement.naturalHeight
        image.value = imageElement
      }
      imageElement.src = imageUrl
    },
    { immediate: true }
  )

  watch(
    [
      isShowExtractedWords,
      isForceShowHighlightBlocks,
      containerState.isResizing,
      currentPageState.positionalText,
      currentPageState.highlightBlockIds,
      currentPageState.highlightBlockScores,
      currentPageState.attributeSources,
      currentPageState.width,
      currentPageState.height,
      canvas,
      image,
      isScaleDefined
    ],
    (
      [
        isShowExtractedWords,
        isForceShowHighlightBlocks,
        containerIsResizing,
        currentPagePositionalText,
        currentPageHighlightBlockIds,
        currentPageHighlightBlockScores,
        currentPageAttributeSources,
        currentPageWidth,
        currentPageHeight,
        canvas,
        image,
        isScaleDefined
      ],
      [
        _oldIsShowExtractedWords,
        _oldIsForceShowHighlightBlocks,
        _oldContainerIsResizing,
        _oldCurrentPagePositionalText,
        _oldCurrentPageHighlightBlockIds,
        _oldCurrentPageHighlightBlockScores,
        oldCurrentPageAttributeSources,
        _oldCurrentPageWidth,
        _oldCurrentPageHeight,
        _oldCanvas,
        _oldImage,
        _oldIsScaleDefined
      ]
    ) => {
      if (containerIsResizing) return

      if (
        typeof isShowExtractedWords === "undefined" ||
        typeof isForceShowHighlightBlocks === "undefined" ||
        typeof currentPagePositionalText === "undefined" ||
        typeof currentPageHighlightBlockIds === "undefined" ||
        typeof currentPageHighlightBlockScores === "undefined" ||
        typeof currentPageAttributeSources === "undefined" ||
        typeof currentPageWidth === "undefined" ||
        typeof currentPageHeight === "undefined" ||
        !canvas ||
        !isScaleDefined
      ) {
        return
      }

      const context = canvas.getContext("2d")
      context.reset()

      if (!image) return

      canvas.width = currentPageWidth
      canvas.height = currentPageHeight
      context.drawImage(image, 0, 0, currentPageWidth, currentPageHeight)

      // Draw bounding boxes for "normal" highlights
      for (const { boundingBox, color } of getBoundingBoxes({
        positionalText: currentPagePositionalText,
        highlightBlockIds: currentPageHighlightBlockIds,
        highlightBlockScores: currentPageHighlightBlockScores,
        attributeSources: currentPageAttributeSources,
        isShowExtractedWords: isShowExtractedWords,
        isForceShowHighlightBlocks: isForceShowHighlightBlocks,
        seeSourceAttributeSlug: seeSourceAttributeSlug.value
      })) {
        context.beginPath()
        context.moveTo(boundingBox[0].x, boundingBox[0].y)
        context.lineTo(boundingBox[1].x, boundingBox[1].y)
        context.lineTo(boundingBox[2].x, boundingBox[2].y)
        context.lineTo(boundingBox[3].x, boundingBox[3].y)
        context.lineWidth = 2
        context.strokeStyle = color
        context.closePath()
        context.stroke()
      }

      // Draw bounding boxes for "tables"
      if (
        isShowExtractedWords &&
        currentPagePositionalText &&
        currentPagePositionalText.length > 0 &&
        currentPagePositionalText[0].tables
      ) {
        for (const table of currentPagePositionalText[0].tables) {
          for (const cell of table.cells) {
            for (const { polygon } of cell.boundingRegions) {
              context.beginPath()
              context.moveTo(polygon[0], polygon[1])
              context.lineTo(polygon[4], polygon[1])
              context.lineTo(polygon[4], polygon[5])
              context.lineTo(polygon[0], polygon[5])
              context.lineWidth = 4
              context.strokeStyle = "rgba(75, 166, 239, 1)"
              context.closePath()
              context.stroke()
            }
          }

          for (const { polygon } of table.boundingRegions) {
            context.beginPath()
            context.moveTo(polygon[0], polygon[1])
            context.lineTo(polygon[4], polygon[1])
            context.lineTo(polygon[4], polygon[5])
            context.lineTo(polygon[0], polygon[5])
            context.lineWidth = 6
            context.strokeStyle = "rgba(75, 166, 239, 1)"
            context.closePath()
            context.stroke()
          }
        }
      }

      /*
       * If we are disabling the SeeSources (therefore resetting the attributeSources)
       * We want to scroll again to the closest highlight if available, we set the isIniiialScrolled to false
       */

      if (oldCurrentPageAttributeSources?.length && !currentPageAttributeSources?.length) {
        isInitialScrolled = false
      }
      if (isInitialScrolled) return

      nextTick(() => {
        const containerElement = containerState.element.value
        if (!containerElement) return

        const canvasWrapperElement = canvasWrapper.value
        if (!canvasWrapperElement) return

        // If a different page's centerRatio is retained, discard it
        if (
          currentPageState.containerCenterRatio.value &&
          currentPageState.containerCenterRatio.value.pageNumber !== pagingState.currentPage.value
        ) {
          currentPageState.containerCenterRatio.value = undefined
        }

        const currentPageContainerCenterRatio = currentPageState.containerCenterRatio.value

        const containerHeight = containerElement.offsetHeight
        const containerWidth = containerElement.offsetWidth

        const canvasWrapperHeight = canvasWrapperElement.offsetHeight
        const canvasWrapperWidth = canvasWrapperElement.offsetWidth
        const canvasWrapperOffsetTop = canvasWrapperElement.offsetTop
        const canvasWrapperOffsetLeft = canvasWrapperElement.offsetLeft

        let scrollTop = (() => {
          if (!currentPageContainerCenterRatio) {
            return canvasWrapperOffsetTop
          }

          return (
            canvasWrapperOffsetTop + canvasWrapperHeight * currentPageContainerCenterRatio.top - containerHeight / 2
          )
        })()

        let scrollLeft = (() => {
          if (!currentPageContainerCenterRatio) {
            // set to center
            return canvasWrapperOffsetLeft + canvasWrapperWidth * 0.5 - containerWidth / 2
          }

          return (
            canvasWrapperOffsetLeft + canvasWrapperWidth * currentPageContainerCenterRatio.left - containerWidth / 2
          )
        })()

        const topHighlightBlockId = (() => {
          if (currentPageAttributeSources.length > 0) {
            return currentPageAttributeSources.reduce((acc, current) => {
              if (current.ocr_block_score > acc.ocr_block_score) {
                return current
              }
              return acc
            }).ocr_block_id
          }

          if (!isForceShowHighlightBlocks) return undefined
          if (currentPageHighlightBlockIds.length === 0) return undefined

          return getTopHighlightBlockId({
            highlightBlockIds: currentPageHighlightBlockIds,
            highlightBlockScores: currentPageHighlightBlockScores
          })
        })()

        if (!topHighlightBlockId) {
          containerElement.scrollTo({
            top: scrollTop,
            left: scrollLeft,
            behavior: "instant"
          })
        } else {
          const scrollPosition = getBoundingBoxScrollPosition(topHighlightBlockId)
          if (scrollPosition) {
            scrollTop = scrollPosition.scrollTop
            scrollLeft = scrollPosition.scrollLeft
          }

          containerElement.scrollTo({
            top: scrollTop,
            left: scrollLeft,
            behavior: "smooth"
          })
        }

        // Set container center ratio
        currentPageState.containerCenterRatio.value = {
          pageNumber: pagingState.currentPage.value,
          top: (scrollTop + containerHeight / 2 - canvasWrapperOffsetTop) / canvasWrapperHeight,
          left: (scrollLeft + containerWidth / 2 - canvasWrapperOffsetLeft) / canvasWrapperWidth
        }

        isInitialScrolled = true
      })
    },
    { immediate: true }
  )

  watch(
    [containerState.width, currentPageState.width, currentPageState.height, scalingState.scale, canvasWrapper, canvas],
    ([containerWidth, currentPageWidth, currentPageHeight, scalingScale, canvasWrapper, canvas]) => {
      if (!containerWidth || !currentPageWidth || !currentPageHeight || !canvasWrapper || !canvas) {
        return
      }

      const wrapperWidth = currentPageWidth * scalingScale
      const wrapperHeight = currentPageHeight * scalingScale

      canvasWrapper.style.width = `${wrapperWidth}px`
      canvasWrapper.style.height = `${wrapperHeight}px`

      const wrapperLeftMargin = wrapperWidth < containerWidth ? (containerWidth - wrapperWidth) / 2 : 0
      canvasWrapper.style.marginLeft = `${wrapperLeftMargin}px`

      canvas.style.transform = `scale(${scalingScale})`
    },
    { immediate: true }
  )

  watch(
    [containerState.width, containerState.height, scalingState.scale],
    ([containerWidth, containerHeight, scalingScale]) => {
      if (
        typeof containerWidth === "undefined" ||
        typeof containerHeight === "undefined" ||
        typeof scalingScale === "undefined"
      ) {
        return
      }

      if (!isInitialScrolled) return

      nextTick(() => {
        if (!currentPageState.containerCenterRatio.value) return
        if (currentPageState.containerCenterRatio.value.pageNumber !== pagingState.currentPage.value) return

        const containerElement = containerState.element.value
        if (!containerElement) return

        const canvasWrapperElement = canvasWrapper.value
        if (!canvasWrapperElement) return

        const canvasWrapperHeight = canvasWrapperElement.offsetHeight
        const canvasWrapperWidth = canvasWrapperElement.offsetWidth
        const canvasWrapperOffsetTop = canvasWrapperElement.offsetTop
        const canvasWrapperOffsetLeft = canvasWrapperElement.offsetLeft

        const topRatio = currentPageState.containerCenterRatio.value.top
        const scrollTop = canvasWrapperOffsetTop + canvasWrapperHeight * topRatio - containerHeight / 2

        const leftRatio = currentPageState.containerCenterRatio.value.left
        const scrollLeft = canvasWrapperOffsetLeft + canvasWrapperWidth * leftRatio - containerWidth / 2

        containerElement.scrollTo({
          top: scrollTop,
          left: scrollLeft,
          behavior: "instant"
        })
      })
    },
    { immediate: true }
  )

  const onContainerElementScroll = () => {
    if (!isInitialScrolled) return

    if (!pagingState.currentPage.value) return

    const containerElement = containerState.element.value
    if (!containerElement) return

    const canvasWrapperElement = canvasWrapper.value
    if (!canvasWrapperElement) return

    const containerHeight = containerElement.offsetHeight
    const containerWidth = containerElement.offsetWidth
    const containerScrollTop = containerElement.scrollTop
    const containerScrollLeft = containerElement.scrollLeft

    const canvasWrapperHeight = canvasWrapperElement.offsetHeight
    const canvasWrapperWidth = canvasWrapperElement.offsetWidth
    const canvasWrapperOffsetTop = canvasWrapperElement.offsetTop
    const canvasWrapperOffsetLeft = canvasWrapperElement.offsetLeft

    if (canvasWrapperHeight === 0 || canvasWrapperWidth === 0) return

    const top = (containerScrollTop + containerHeight / 2 - canvasWrapperOffsetTop) / canvasWrapperHeight
    const left = (containerScrollLeft + containerWidth / 2 - canvasWrapperOffsetLeft) / canvasWrapperWidth

    currentPageState.containerCenterRatio.value = {
      pageNumber: pagingState.currentPage.value,
      top,
      left
    }
  }

  const onContainerElementWheel = event => {
    if (!event.ctrlKey) return

    event.preventDefault()

    if (event.deltaY < 0) {
      scalingState.zoomInContinuous()
    } else {
      scalingState.zoomOutContinuous()
    }
  }

  watch(
    containerState.element,
    (containerElement, oldContainerElement) => {
      if (oldContainerElement) {
        oldContainerElement.removeEventListener("scroll", onContainerElementScroll)
        oldContainerElement.removeEventListener("wheel", onContainerElementWheel)
      }

      if (!containerElement) return

      containerElement.addEventListener("scroll", event => onContainerElementScroll(event, "scroll"))
      containerElement.addEventListener("wheel", event => onContainerElementWheel(event, "wheel"))
    },
    { immediate: true }
  )

  onBeforeUnmount(() => {
    if (containerState.element.value) {
      containerState.element.value.removeEventListener("scroll", onContainerElementScroll)
      containerState.element.value.removeEventListener("wheel", onContainerElementWheel)
    }
  })
}
